import { useEffect, useState } from "react";
import { SearchBox } from "../../../components/Search";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { BrushStart, ChartType } from "../../../models/chart_model";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { getHaryanaMonthlyPeak } from "../../../Rest_api/RestApi";
import { HarayanaMonthlyPeakData } from "../../../models/rajasthan";
import Loading from "../../../components/Loading";

const chartSuffixes = [
  { suffix: "peak", displayName: "Peak", stackSuffix: "peak" },
  { suffix: "average", displayName: "Average", stackSuffix: "average" },
];

const selectedChartType = [
  {
    value: ChartType.Bar,
    label: "Standard Bar",
    stackId: undefined,
  },
  { value: ChartType.Line, label: "Line", stackId: undefined },
];

const MonthlyPeak = () => {
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedSuffix, setSelectedSuffix] = useState([
    chartSuffixes[0],
    chartSuffixes[1],
  ]);

  const [data, setData] = useState<HarayanaMonthlyPeakData[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState<
    { value: string; label: string }[]
  >([] as any);

  async function _handlefetchData() {
    setLoading(true);
    try {
      // Provide start and end dates for the API
      const startDate = "2025-01-01"; // Adjust as needed
      const endDate = "2025-12-31";   // Adjust as needed
      const newData = await getHaryanaMonthlyPeak(startDate, endDate);
      setData(newData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // Initialize with some available months when data loads (same as forecast)
    if (data.length > 0 && selectedMonth.length === 0) {
      const dynamicMonths = getDynamicMonths();
      setSelectedMonth(dynamicMonths.slice(0, 12)); // Show first 12 months by default
    }
  }, [data]);

  useEffect(() => {
    _handlefetchData();
  }, []);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    ExchangeColors: string[],
    type?: string
  ) => {
    return {
      dataKey: `${key}_${suffix}_${"MW".toLowerCase()}`,
      name: `${displayName}`,
      legendColor: undefined,
      stroke: displayName === "Peak" ? "#F1935C" : "#34656D",
      type:
        displayName === "Peak"
          ? selectedChartType[0].value
          : selectedChartType[1].value,
      stackId:
        selectedChartType[index].stackId !== undefined
          ? stackSuffix
          : undefined,
    };
  };

  const getDynamicMonths = () => {
    if (!data || data.length === 0) return [];

    // Since the API now maps month to "Jan 25" format, we can use it directly
    const uniqueMonths = Array.from(new Set(data.map((item) => item.month)));

    return uniqueMonths
      .map((month) => ({
        value: String(month).toUpperCase(), // Convert "Jan 25" to "JAN 25"
        label: String(month), // Keep original format for display
      }))
      .sort((a, b) => {
        // Extract month and year for proper sorting
        const [monthA, yearA] = a.label.split(' ');
        const [monthB, yearB] = b.label.split(' ');
        
        if (yearA !== yearB) {
          return yearA.localeCompare(yearB);
        }
        
        const monthOrder = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
      });
  };

  useEffect(() => {
    let filteredData = data;

    if (selectedMonth.length > 0) {
      filteredData = data.filter((item: HarayanaMonthlyPeakData) => {
        // Convert item.month to the same format as selectedMonth values
        const itemMonthValue = String(item.month).toUpperCase();
        
        return selectedMonth.some(
          (date: { value: string }) => date.value === itemMonthValue
        );
      });
    }

    // Create month_year field for chart display using the formatted month from API
    const processedData = filteredData.map(item => ({
      ...item,
      month_year: item.month // Use the API formatted month directly
    }));

    const par: ChartDataParams = {
      keys: ["month_year"],
      groupBy: [],
      volumeKey: ``,
      aggregationKeys: ["peak_mw", "average_mw"], // Using the correct field names
      priceKey: "",
      data: processedData,
    };
    
    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [data, selectedMonth]);

  return (
    <>
      {loading && <Loading />}
      <div className="dash-card col-span-1 md:col-span-6  h-full w-full">
        <div className="flex justify-between items-center mb-2">
          <div className="gas-heading mb-4">
            <h2 className="chartHeading">Monthly Historic Demand</h2>
          </div>

          <div className="w-[50%] ">
            <SearchBox
              options={getDynamicMonths()}
              isMany={true}
              placeholder="Search Month"
              selectedOptions={selectedMonth}
              onChange={(item: any) => {
                setSelectedMonth(
                  item.map(
                    (opt: any) =>
                      getDynamicMonths().find((date) => date.value === opt.value) ??
                      opt
                  )
                );
              }}
            />
          </div>
        </div>

        <ReMixChart
          data={chartData}
          showBrush={chartData.length > 96 * 7 ? true : undefined}
          brushStart={BrushStart.Start}
          brushIndex={
            chartData.length > 96 * 7
              ? { startIndex: 0, endIndex: 96 * 7 }
              : undefined
          }
          isTimeSlot={false}
          onlyTitle={false}
          barCategoryGap={10}
          unit={"MW"}
          yAxisLabel={"MW"}
          legends={selectedSuffix.flatMap(
            ({ suffix, displayName, stackSuffix }) =>
              chartKeys.map(
                (key, index) =>
                  createChartObject(
                    key,
                    index,
                    suffix,
                    displayName,
                    stackSuffix,
                    ExchangeColors
                  ) as any
              )
          )}
          xDataKey={"month_year"}
          xTick={undefined}
          secondXDataKey={undefined}
        />
      </div>
    </>
  );
};

export default MonthlyPeak;